import { Feature } from '@/@types/featureSwitch';
import { useFeature } from '@/apiHooks/featureSwitches.hook';
import { getAndroidAppBuild, getNativePlatform } from '@/components/native-bridge';
import { isAlternativeBillingBridgeAvailable } from '@/services/nativeBridge/NativeBridgeService';

const MIN_BLOCKED_ANDROID_BUILD = 1557;
const MIN_ALTERNATIVE_BILLING_ANDROID_BUILD = 1558;

/**
 * Wohnen+ Light Android rollout logic:
 *
 * IF
 *   BLOCK_WOHNEN_PLUS_LIGHT_UPSELL_ANDROID is ON
 *   AND the user is on Android
 *   AND the app build is >= 1557
 * THEN
 *   Do not show the Wohnen+ Light upsell modal.
 *
 * ELSE IF
 *   BIZ_9847_ANDROID_EXTERNAL_TRANSACTION_TOKEN is ON
 *   AND the user is on Android
 *   AND the app build is >= 1558
 * THEN
 *   Show the modal only when the Alternative Billing native bridge is available.
 *   On purchase, obtain the external transaction token before starting checkout.
 *
 * ELSE
 *   Show the modal and use the existing checkout flow without Alternative Billing.
 *
 * @returns Whether Alternative Billing is required and whether the upsell must be blocked.
 */
export const useAndroidAlternativeBilling = () => {
  const blockerEnabled = useFeature(Feature.BLOCK_WOHNEN_PLUS_LIGHT_UPSELL_ANDROID);
  const alternativeBillingEnabled = useFeature(Feature.BIZ_9847_ANDROID_EXTERNAL_TRANSACTION_TOKEN);
  const build = getAndroidAppBuild();

  if (getNativePlatform() !== 'android' || build === null) {
    return { isRequired: false, isBlocked: false };
  }

  const isBlockedByRollout = blockerEnabled && build >= MIN_BLOCKED_ANDROID_BUILD;
  const isRequired = alternativeBillingEnabled && build >= MIN_ALTERNATIVE_BILLING_ANDROID_BUILD;

  return {
    isRequired,
    isBlocked: isBlockedByRollout || (isRequired && !isAlternativeBillingBridgeAvailable()),
  };
};
